import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SimpleAdminDashboard = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // Filtres et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  useEffect(() => {
    verifyAndLoadDashboard();
  }, []);
  
  const verifyAndLoadDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setIsLoggedIn(true);
        loadDashboardData();
      } else {
        setIsLoggedIn(false);
      }
    } catch (error) {
      console.error('Erreur de vérification:', error);
      setIsLoggedIn(false);
    }
  };
  
  const loadDashboardData = async () => {
    try {
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        credentials: 'include'
      });
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      
      const bookingsRes = await fetch(`${API_URL}/api/admin/bookings`, {
        credentials: 'include'
      });
      if (bookingsRes.ok) {
        const bookingsData = await bookingsRes.json();
        setBookings(bookingsData);
      }
    } catch (error) {
      console.error('Erreur de chargement:', error);
    }
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        setIsLoggedIn(true);
        loadDashboardData();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Email ou mot de passe incorrect');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Erreur de déconnexion:', error);
    }
    setIsLoggedIn(false);
    setStats(null);
    setBookings([]);
  };
  
  const startEdit = (booking) => {
    setEditingBooking(booking.id);
    setEditValues({
      price: booking.price || 0,
      commission: booking.commission || 0,
      driver_assigned: booking.driver_assigned || false,
      commission_paid: booking.commission_paid || false,
      status: booking.status || 'en_attente'
    });
  };
  
  const cancelEdit = () => {
    setEditingBooking(null);
    setEditValues({});
  };
  
  const saveEdit = async (bookingId) => {
    try {
      console.log('Saving booking:', bookingId, 'with data:', editValues);
      
      const response = await fetch(`${API_URL}/api/admin/bookings/${bookingId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(editValues)
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Update successful:', result);
        await loadDashboardData();
        setEditingBooking(null);
        setEditValues({});
        alert('✓ Réservation mise à jour avec succès !');
      } else {
        const errorData = await response.json();
        console.error('Update failed:', errorData);
        alert(`Erreur lors de la mise à jour: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Erreur de mise à jour:', error);
      alert(`Erreur de mise à jour: ${error.message}`);
    }
  };
  
  // Fonctions de filtrage et tri
  const getFilteredAndSortedBookings = () => {
    let filtered = [...bookings];
    
    // Filtre de recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => 
        booking.name?.toLowerCase().includes(term) ||
        booking.email?.toLowerCase().includes(term) ||
        booking.phone?.toLowerCase().includes(term) ||
        booking.pickup?.toLowerCase().includes(term) ||
        booking.destination?.toLowerCase().includes(term)
      );
    }
    
    // Filtre par date
    const now = new Date();
    if (dateFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.created_at || booking.date);
        return bookingDate >= weekAgo;
      });
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.created_at || booking.date);
        return bookingDate >= monthAgo;
      });
    } else if (dateFilter === 'custom' && startDate && endDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.created_at || booking.date);
        return bookingDate >= new Date(startDate) && bookingDate <= new Date(endDate);
      });
    }
    
    // Tri
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'timestamp') {
        aVal = new Date(a.created_at || a.date);
        bVal = new Date(b.created_at || b.date);
      } else if (sortBy === 'price') {
        aVal = parseFloat(a.price) || 0;
        bVal = parseFloat(b.price) || 0;
      } else {
        aVal = a[sortBy] || '';
        bVal = b[sortBy] || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  };
  
  const filteredBookings = getFilteredAndSortedBookings();
  
  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBookings = filteredBookings.slice(startIndex, endIndex);
  
  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, startDate, endDate, sortBy, sortOrder]);
  
  // Page de login
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              🔐 Admin Dashboard
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Brutaxi Belgium - Administration
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-t-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm"
                  placeholder="Email"
                />
              </div>
              <div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-b-md relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 focus:z-10 sm:text-sm"
                  placeholder="Mot de passe"
                />
              </div>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-black bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
  
  // Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-yellow-500">BRUTAXI</h1>
            <p className="text-sm text-gray-400">Dashboard Administration</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm"
          >
            Déconnexion
          </button>
        </div>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-3xl">📊</span>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Réservations</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.total_bookings}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-3xl">💰</span>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Revenu Total</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.total_revenue}€</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-3xl">📅</span>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Aujourd'hui</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.today_bookings}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="text-3xl">📋</span>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Tentatives</dt>
                      <dd className="text-2xl font-semibold text-gray-900">{stats.total_attempts}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Historique des réservations */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              📚 Historique des Réservations
            </h3>
            
            {/* Barre de filtres */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Recherche */}
              <div className="lg:col-span-2">
                <input
                  type="text"
                  placeholder="🔍 Rechercher (nom, email, trajet...)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                />
              </div>
              
              {/* Filtre par date */}
              <div>
                <select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                >
                  <option value="all">📅 Toutes les dates</option>
                  <option value="week">7 derniers jours</option>
                  <option value="month">30 derniers jours</option>
                  <option value="custom">Période personnalisée</option>
                </select>
              </div>
              
              {/* Tri */}
              <div>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                >
                  <option value="timestamp-desc">📅 Plus récent</option>
                  <option value="timestamp-asc">📅 Plus ancien</option>
                  <option value="price-desc">💰 Prix décroissant</option>
                  <option value="price-asc">💰 Prix croissant</option>
                </select>
              </div>
            </div>
            
            {/* Dates personnalisées */}
            {dateFilter === 'custom' && (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date fin</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 text-sm"
                  />
                </div>
              </div>
            )}
            
            {/* Compteur de résultats */}
            <div className="mt-4 text-sm text-gray-600">
              {filteredBookings.length} réservation(s) trouvée(s) • Affichage de {startIndex + 1} à {Math.min(endIndex, filteredBookings.length)}
            </div>
          </div>
          
          {/* Tableau */}
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trajet</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chauffeur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comm. Payée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentBookings.map((booking) => {
                    const isEditing = editingBooking === booking.id;
                    
                    return (
                      <tr key={booking.id} className={isEditing ? 'bg-yellow-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{booking.name}</div>
                          <div className="text-sm text-gray-500">{booking.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {booking.date} {booking.time}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div className="truncate max-w-xs">
                            {booking.pickup} → {booking.destination}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="number"
                              value={editValues.price}
                              onChange={(e) => setEditValues({...editValues, price: parseFloat(e.target.value)})}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              step="0.01"
                            />
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">{booking.price}€</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <input
                              type="number"
                              value={editValues.commission}
                              onChange={(e) => setEditValues({...editValues, commission: parseFloat(e.target.value)})}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                              step="0.01"
                            />
                          ) : (
                            <span className="text-sm text-gray-900">{booking.commission || 0}€</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <select
                              value={editValues.driver_assigned ? 'true' : 'false'}
                              onChange={(e) => setEditValues({...editValues, driver_assigned: e.target.value === 'true'})}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="false">Non attribué</option>
                              <option value="true">Attribué</option>
                            </select>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              booking.driver_assigned ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {booking.driver_assigned ? 'Attribué' : 'Non attribué'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <select
                              value={editValues.commission_paid ? 'true' : 'false'}
                              onChange={(e) => setEditValues({...editValues, commission_paid: e.target.value === 'true'})}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="false">Non payée</option>
                              <option value="true">Payée</option>
                            </select>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              booking.commission_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {booking.commission_paid ? 'Payée' : 'Non payée'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {isEditing ? (
                            <select
                              value={editValues.status || 'en_attente'}
                              onChange={(e) => setEditValues({...editValues, status: e.target.value})}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              <option value="en_attente">En attente</option>
                              <option value="confirmee">Confirmée</option>
                              <option value="annulee">Annulée</option>
                              <option value="terminee">Terminée</option>
                            </select>
                          ) : (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              booking.status === 'confirmee' ? 'bg-green-100 text-green-800' :
                              booking.status === 'annulee' ? 'bg-red-100 text-red-800' :
                              booking.status === 'terminee' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.status === 'confirmee' ? 'Confirmée' :
                               booking.status === 'annulee' ? 'Annulée' :
                               booking.status === 'terminee' ? 'Terminée' :
                               'En attente'}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isEditing ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => saveEdit(booking.id)}
                                className="text-green-600 hover:text-green-900 font-medium"
                              >
                                ✓ Sauver
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="text-red-600 hover:text-red-900 font-medium"
                              >
                                ✗ Annuler
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEdit(booking)}
                              className="text-blue-600 hover:text-blue-900 font-medium"
                            >
                              ✏️ Modifier
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Pagination */}
          {filteredBookings.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur <span className="font-medium">{totalPages}</span>
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ‹ Précédent
                    </button>
                    
                    {/* Pages numbers */}
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant ›
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimpleAdminDashboard;
